import { Box, Drawer } from '@mui/material';
import styled, { css } from 'styled-components';

import { IconButton } from '@/shared/components/ui/button/icon-button/icon-button';

const openedMixin = css`
  width: 17.5rem;
  transition: ${({ theme }) =>
    theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    })};
  overflow-x: hidden;
`;

const closedMixin = css`
  transition: ${({ theme }) =>
    theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    })};
  overflow-x: hidden;
  width: 5.25rem;
`;

type CustomDrawerProps = {
  open: boolean;
};

export const CustomDrawer = styled(Drawer)<CustomDrawerProps>`
  && {
    width: ${({ open }) => (open ? 17.5 : 5.25)}rem;
    flex-shrink: 0;
    white-space: nowrap;
    transition: ${({ theme }) =>
      theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      })};

    & .MuiDrawer-paper {
      border-top-right-radius: ${({ theme }) => theme.shape.drawerRadius};
      border-bottom-right-radius: ${({ theme }) => theme.shape.drawerRadius};
      padding: 1.25rem;
      box-shadow: ${({ theme }) => theme.shadow.drawer};

      ${({ open }) => css`
        ${open ? openedMixin : closedMixin};
      `}
    }
  }
`;

type ExpandCollapseButtonProps = {
  $open?: boolean;
};

export const ExpandCollapseButton = styled(
  IconButton,
)<ExpandCollapseButtonProps>`
  && {
    position: fixed;
    top: 1.75rem;
    left: ${({ $open }) => ($open ? 17.5 : 5.25)}rem;
    transform: translateX(-50%);
    z-index: 1201;
    width: 1.5rem;
    height: 1.5rem;
    padding: 0.25rem;
    background: ${({ theme }) => theme.palette.background.paper};
    border: 1px solid ${({ theme }) => theme.palette.divider};
    border-radius: 50%;
    color: ${({ theme }) => theme.palette.text.primary};
    transition: ${({ theme, $open }) =>
      theme.transitions.create(['border', 'left'], {
        easing: theme.transitions.easing.sharp,
        duration:
          theme.transitions.duration[
            $open ? 'enteringScreen' : 'leavingScreen'
          ],
      })} !important;

    &:hover {
      background: ${({ theme }) => theme.palette.background.paper};
      border: 1px solid ${({ theme }) => theme.palette.text.primary};
    }

    & > svg {
      width: 1.25rem;
      height: 1.25rem;

      & path {
        stroke: ${({ theme }) => theme.palette.text.primary};
      }
    }
  }
`;

export const TopContent = styled(Box)`
  && {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    flex: 1;
  }
`;

type LogoContainerProps = {
  $open?: boolean;
};

export const LogoContainer = styled(Box)<LogoContainerProps>`
  && {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 1rem;
    min-width: 0;
    width: 100%;

    & > * {
      min-width: fit-content;
      transition: 0.3s ease-in-out;
    }

    > :nth-of-type(2) {
      opacity: ${({ $open }) => ($open ? 1 : 0)};
    }

    & > svg {
      max-height: ${({ $open }) => ($open ? '2.5rem' : '1.4rem')};
      max-width: 2.5rem;
      margin: ${({ $open }) => ($open ? 0 : '0.55rem 0')};
    }
  }
`;

export const SectionsContainer = styled(Box)`
  && {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }
`;

export const BottomContent = styled(Box)`
  && {
    display: flex;
    flex-direction: column;
    margin-top: 1.75rem;
    gap: 1rem;
  }
`;
